import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiService from '../services/api';

const POLL_INTERVAL_MS = 60_000;

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 2, py: 0.75, mx: -3, mt: -3, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function UsageStat({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Grid>
  );
}

function formatGb(gb: number): string {
  if (gb < 1) return `${(gb * 1024).toFixed(0)} MB`;
  return `${gb.toFixed(2)} GB`;
}

function billingPeriod(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(start)} – ${fmt(now)}`;
}

type Props = {
  pppoeUsername: string;
};

export default function SupaMonthlyUsage({ pppoeUsername }: Props) {
  const [data, setData] = useState<{ upload_gb: number; download_gb: number; session_count: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await apiService.adminGetRadiusMonthlyUsage(pppoeUsername);
      setData(result);
      setError(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Failed to fetch usage');
    } finally {
      setLoading(false);
      setLastChecked(new Date());
    }
  }, [pppoeUsername]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load]);

  const handleRefresh = () => {
    setLoading(true);
    load();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
  };

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <SectionLabel>Monthly usage</SectionLabel>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: data ? 2 : 0 }}>
        <Typography variant="caption" color="text.secondary">
          {billingPeriod()}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {loading && <CircularProgress size={14} />}
          {lastChecked && !loading && (
            <Typography variant="caption" color="text.secondary">
              Updated {lastChecked.toLocaleTimeString()}
            </Typography>
          )}
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {data && (
        <Grid container spacing={2}>
          <UsageStat label="Upload" value={formatGb(data.upload_gb)} />
          <UsageStat label="Download" value={formatGb(data.download_gb)} />
          <UsageStat label="Total" value={formatGb(data.upload_gb + data.download_gb)} />
          <UsageStat label="Sessions" value={String(data.session_count)} />
        </Grid>
      )}
    </Paper>
  );
}
